import { useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

export default function KelulusanCard({ student, onUpdated }) {
  const k = student.kelulusan || {};
  const [status, setStatus] = useState(k.status || "");
  const [nomorSk, setNomorSk] = useState(k.nomor_sk || "");
  const [tanggal, setTanggal] = useState(k.tanggal || "");
  const [nomorIjazah, setNomorIjazah] = useState(k.nomor_ijazah || "");
  const [rataRata, setRataRata] = useState(k.rata_rata_nilai || "");
  const [melanjutkan, setMelanjutkan] = useState(k.melanjutkan_ke || "");
  const [catatan, setCatatan] = useState(k.catatan || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/students/${student.id}/kelulusan`, {
        status: status || null,
        nomor_sk: nomorSk,
        tanggal,
        nomor_ijazah: nomorIjazah,
        rata_rata_nilai: rataRata,
        melanjutkan_ke: melanjutkan,
        catatan,
      });
      toast.success("Data kelulusan tersimpan");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setLoading(false);
    }
  };

  const badge = status === "lulus"
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : status === "tidak_lulus"
    ? "bg-rose-100 text-rose-800 border-rose-300"
    : "bg-slate-100 text-slate-600 border-slate-200";
  const label = status === "lulus" ? "LULUS" : status === "tidak_lulus" ? "TIDAK LULUS" : "Belum Ditetapkan";

  return (
    <Card data-testid="kelulusan-card">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Award className="w-5 h-5 text-blue-700" /> Keterangan Kelulusan
          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badge}`} data-testid="kelulusan-badge">{label}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-700">Status Kelulusan</Label>
            <Select value={status || "_none"} onValueChange={(v) => setStatus(v === "_none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 h-10" data-testid="kelulusan-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Belum Ditetapkan</SelectItem>
                <SelectItem value="lulus">LULUS</SelectItem>
                <SelectItem value="tidak_lulus">TIDAK LULUS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700">Tanggal Kelulusan</Label>
            <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="mt-1.5 h-10" data-testid="kelulusan-tanggal-input" />
          </div>
          <div>
            <Label className="text-slate-700">Nomor Surat Keputusan</Label>
            <Input value={nomorSk} onChange={(e) => setNomorSk(e.target.value)} className="mt-1.5 h-10" placeholder="Contoh: 421/SK/SMP-01/2026" data-testid="kelulusan-sk-input" />
          </div>
          <div>
            <Label className="text-slate-700">Nomor Ijazah</Label>
            <Input value={nomorIjazah} onChange={(e) => setNomorIjazah(e.target.value)} className="mt-1.5 h-10" data-testid="kelulusan-ijazah-input" />
          </div>
          <div>
            <Label className="text-slate-700">Rata-rata Nilai Ujian</Label>
            <Input value={rataRata} onChange={(e) => setRataRata(e.target.value)} className="mt-1.5 h-10" placeholder="Contoh: 82.50" data-testid="kelulusan-rata-input" />
          </div>
          <div>
            <Label className="text-slate-700">Melanjutkan ke</Label>
            <Input value={melanjutkan} onChange={(e) => setMelanjutkan(e.target.value)} className="mt-1.5 h-10" placeholder="Contoh: SMA Negeri 1 Kalianda" data-testid="kelulusan-melanjutkan-input" />
          </div>
        </div>
        <div>
          <Label className="text-slate-700">Catatan</Label>
          <Textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} className="mt-1.5" data-testid="kelulusan-catatan-input" />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="save-kelulusan-button">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Simpan Kelulusan
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
